import { createMemoryRouter, Navigate } from "react-router-dom";
import { lazy, Suspense, ComponentType } from "react";
import { AppLayout } from "@/ui/module_api";
import { LoadingSpinner } from "@/ui/module_api";
import { RootScreen } from "@/features/module_api";
import { useStore } from "@/state/legacy_module_api";
import { Outlet } from "react-router-dom";

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex-1 flex items-center justify-center">
    <LoadingSpinner size="lg" />
  </div>
);

// Helper to create lazy loaded route element
function lazyLoad(importFn: () => Promise<{ default: ComponentType }>) {
  const LazyComponent = lazy(importFn);
  return (
    <Suspense fallback={<LoadingFallback />}>
      <LazyComponent />
    </Suspense>
  );
}

// Simple auth guard: redirects to /welcome if no apiToken
const RequireAuth = () => {
  const token = useStore((state) => state.apiToken);
  return token ? <Outlet /> : <Navigate to="/welcome" replace />;
};

// Redirects to / if already authenticated
const RedirectIfAuthed = () => {
  const token = useStore((state) => state.apiToken);
  return token ? <Navigate to="/" replace /> : <Outlet />;
};

// Show RootScreen if authenticated, otherwise redirect to welcome
const IndexRoute = () => {
  const token = useStore((state) => state.apiToken);
  if (!token) {
    return <Navigate to="/welcome" replace />;
  }
  return <RootScreen />;
};

export const router = createMemoryRouter([
  {
    path: "/",
    element: <AppLayout />,
    children: [
      // Index route: show RootScreen if authenticated, otherwise redirect to welcome
      { index: true, element: <IndexRoute /> },

      // Core routes (redirect to / if already authenticated)
      {
        element: <RedirectIfAuthed />,
        children: [
          { path: "welcome", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.WelcomeScreen }))) },
          { path: "link-device", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.LinkDeviceCodeEntryScreen }))) },
          { path: "restore", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.RestoreScreen }))) },
          // Auth & Registration (public)
          { path: "create-user-view", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.CreateUserView }))) },
          { path: "anon-registration", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.AnonRegistrationScreen }))) },
        ],
      },
      // Keep /root as alias for backwards compatibility
      { path: "root", element: <Navigate to="/" replace /> },
      { path: "settings", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.SettingsScreen }))) },

      // Protected routes (require apiToken)
      {
        element: <RequireAuth />,
        children: [
          // Thread
          { path: "thread-view", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.ThreadView }))) },
          { path: "start-thread", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.StartThreadScreen }))) },
          { path: "message-details", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.MessageDetailsScreen }))) },

          // Communities
          { path: "communities-list", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.CommunitiesListView }))) },
          { path: "communities-create", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.CommunityCreateView }))) },
          { path: "communities-details", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.CommunityDetailsView }))) },
          { path: "communities-members", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.CommunityMembersView }))) },
          { path: "communities-add-member", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.CommunitiesAddMember }))) },

          // Groups
          { path: "communities-group-create", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.GroupCreateView }))) },
          { path: "create-group", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.CreateGroupScreen }))) },
          { path: "group-details", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.GroupDetailsScreen }))) },

          // Users
          { path: "search-user-view", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.SearchUserView }))) },
          { path: "search-user", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.SearchUserView }))) },
          { path: "add-friends", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.AddFriendsScreen }))) },
          { path: "scan-qr", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.ScanQrScreen }))) },
          { path: "show-my-qr", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.ShowMyQrScreen }))) },

          // Profile & Settings
          { path: "my-profile", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.MyProfileScreen }))) },
          { path: "settings-notification", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.SettingsNotificationScreen }))) },
          { path: "manage-lock-codes", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.ManageLockCodes }))) },
          { path: "linked-devices", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.LinkedDevicesScreen }))) },

          // Misc
          { path: "backup-recovery", element: lazyLoad(() => import("@/features/module_api").then((module) => ({ default: module.BackupRecoveryScreen }))) },
        ],
      },
      // Catch-all: redirect unknown routes
      { path: "*", element: <Navigate to="/welcome" replace /> },
    ],
  },
]);

// Export route types for type-safe navigation
export type RoutePaths =
  | "/welcome"
  | "/link-device"
  | "/restore"
  | "/create-user-view"
  | "/"
  | "/settings"
  | "/anon-registration"
  | "/thread-view"
  | "/start-thread"
  | "/message-details"
  | "/communities-list"
  | "/communities-create"
  | "/communities-details"
  | "/communities-members"
  | "/communities-add-member"
  | "/communities-group-create"
  | "/create-group"
  | "/group-details"
  | "/search-user-view"
  | "/search-user"
  | "/add-friends"
  | "/scan-qr"
  | "/show-my-qr"
  | "/my-profile"
  | "/settings-notification"
  | "/manage-lock-codes"
  | "/linked-devices"
  | "/backup-recovery";

export type TabsPaths =
  | "/threads-tab"
  | "/users-tab"
  | "/communities-tab";
